import React, { useEffect, useState } from "react";
import {
  Box, Button, Grid, IconButton, Typography, MenuItem, TextField,
  Divider, alpha, Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import TvIcon from "@mui/icons-material/Tv";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetDeviceQuery,
  useGetPlaylistGroupsQuery,
  useAssignDevicePlaylistGroupMutation,
} from "../../../Api/attoDeskApi";
import { IDevice, IPlaylistGroup } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import PageLoader from "../../../Components/Loader/PageLoader";
import SignageSection, {
  signageLabelSx, signageInputSx,
} from "../../../Components/ui/SignageSection";

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography sx={signageLabelSx}>{label}</Typography>
    <Typography sx={{ fontSize: 14, color: "#0f172a", fontWeight: 600 }}>
      {value ?? "—"}
    </Typography>
  </Grid>
);

const DeviceView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const deviceId = Number(id);
  const { showMessage, showErrorMessage } = useNotifier();

  const { data, isLoading, refetch } = useGetDeviceQuery(deviceId);
  const { data: groupsData } = useGetPlaylistGroupsQuery();
  const [assignGroup, { isLoading: isAssigning }] = useAssignDevicePlaylistGroupMutation();

  const device = data?.data as unknown as IDevice | undefined;
  const groups: IPlaylistGroup[] = (Array.isArray(groupsData?.data)
    ? ((groupsData?.data as unknown) as IPlaylistGroup[])
    : []).filter(g => g.is_active);

  const [selectedGroupId, setSelectedGroupId] = useState<number | "">("");

  useEffect(() => {
    if (device) setSelectedGroupId(device.playlist_group_id ?? "");
  }, [device]);

  const handleAssign = async () => {
    try {
      const res = await assignGroup({
        id: deviceId,
        playlist_group_id: selectedGroupId === "" ? null : Number(selectedGroupId),
      }).unwrap();
      if (res.status) {
        showMessage("Playlist group assigned");
        refetch();
      }
    } catch {
      showErrorMessage("Failed to assign playlist group");
    }
  };

  if (isLoading || !device) return <PageLoader height="20vh" />;
  const online = device.status === "online";

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, py: 0, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.15)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
          <IconButton onClick={() => navigate("/device/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, backdropFilter: "blur(10px)",
              bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
                boxShadow: "0 2px 8px rgba(0,131,143,0.4)",
              }}>
                <TvIcon sx={{ fontSize: 16, color: "#fff" }} />
              </Box>
              <Typography fontSize={18} fontWeight={800} color="#fff" letterSpacing="-0.02em">
                {device.device_name}
              </Typography>
              <Typography fontSize={12} color="rgba(255,255,255,0.45)" sx={{ ml: 1, fontFamily: "monospace" }}>
                {device.device_code}
              </Typography>
            </Box>
            <Typography fontSize={11} color="rgba(255,255,255,0.45)" mt={0.25} ml={5.5}>
              Device details
            </Typography>
          </Box>
          <Button
            variant="contained" disableElevation size="small"
            onClick={() => navigate(`/device/edit/${device.id}`)}
            startIcon={<EditSharpIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
              background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
              "&:hover": { background: "linear-gradient(135deg, #006064 0%, #00838f 100%)" },
            }}
          >
            Edit
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <SignageSection
              icon={<InfoOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />}
              title="Device Information"
              accentColor="#3b82f6"
            >
              <Grid container spacing={2.5}>
                <Field label="Device Name" value={device.device_name} />
                <Field label="Device Code" value={device.device_code} />
                <Field label="Location" value={device.location || "—"} />
                <Field label="UUID" value={<Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#475569" }}>{device.uuid}</Typography>} />
                <Field label="App Version" value={device.app_version || "—"} />
                <Field
                  label="Last Seen"
                  value={device.last_seen_at ? new Date(device.last_seen_at).toLocaleString() : "Never"}
                />
              </Grid>
            </SignageSection>

            <SignageSection
              icon={<PlaylistPlayIcon sx={{ fontSize: 18, color: "#7c3aed" }} />}
              title="Assign Playlist Group"
              accentColor="#7c3aed"
              subtitle="Set or change the playlist group this device plays"
            >
              <Grid container spacing={2.5} alignItems="flex-end">
                <Grid item xs={12} sm={8}>
                  <Typography sx={signageLabelSx}>Playlist Group</Typography>
                  <TextField
                    select fullWidth size="small"
                    value={selectedGroupId}
                    onChange={(e) =>
                      setSelectedGroupId(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    sx={signageInputSx}
                  >
                    <MenuItem value="">No Playlist Group</MenuItem>
                    {groups.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.name} · {g.playlists_count ?? 0} playlists · {g.playback_mode}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth variant="contained" disableElevation
                    onClick={handleAssign} disabled={isAssigning}
                    sx={{
                      borderRadius: 2.5, textTransform: "none",
                      fontSize: 12, fontWeight: 700, py: 1,
                      background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
                      "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
                    }}
                  >
                    {isAssigning ? "Saving…" : "Apply"}
                  </Button>
                </Grid>
              </Grid>
            </SignageSection>
          </Grid>

          <Grid item xs={12} lg={4}>
            <SignageSection
              icon={<SettingsIcon sx={{ fontSize: 18, color: "#00838f" }} />}
              title="Current State"
              accentColor="#00838f"
            >
              <Box sx={{
                p: 2.5, borderRadius: 2.5,
                bgcolor: online ? alpha("#10b981", 0.06) : alpha("#ef4444", 0.06),
                border: `1px solid ${online ? alpha("#10b981", 0.2) : alpha("#ef4444", 0.2)}`,
                mb: 2,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    size="small"
                    icon={<FiberManualRecordIcon sx={{ fontSize: 9 }} />}
                    label={online ? "Online" : "Offline"}
                    sx={{
                      height: 24, fontSize: 11, fontWeight: 700,
                      bgcolor: online ? "#ecfdf5" : "#fef2f2",
                      color: online ? "#047857" : "#b91c1c",
                      border: `1px solid ${online ? "#a7f3d0" : "#fecaca"}`,
                      "& .MuiChip-icon": { color: online ? "#10b981" : "#ef4444", ml: 0.75 },
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: 11, color: "#64748b", mt: 1 }}>
                  {device.last_seen_at
                    ? `Last heartbeat ${new Date(device.last_seen_at).toLocaleString()}`
                    : "This device has never reported in"}
                </Typography>
              </Box>

              <Typography sx={signageLabelSx}>Current Playlist Group</Typography>
              {device.playlist_group ? (
                <Box sx={{
                  p: 2, borderRadius: 2.5,
                  bgcolor: alpha("#7c3aed", 0.04),
                  border: `1px solid ${alpha("#7c3aed", 0.18)}`,
                  display: "flex", alignItems: "center", gap: 1.5,
                  cursor: "pointer",
                }}
                onClick={() => navigate(`/playlist-groups/edit/${device.playlist_group!.id}`)}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 2,
                    bgcolor: alpha("#7c3aed", 0.15),
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <PlaylistPlayIcon sx={{ color: "#7c3aed", fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                      {device.playlist_group.name}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#7c3aed", fontWeight: 600 }}>
                      {device.playlist_group.playback_mode}
                      {typeof device.playlist_group.playlists_count === "number"
                        ? ` · ${device.playlist_group.playlists_count} playlist${device.playlist_group.playlists_count === 1 ? "" : "s"}`
                        : ""}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{
                  p: 2, borderRadius: 2.5, bgcolor: "#f8fafc",
                  border: "1px dashed #cbd5e1", textAlign: "center",
                }}>
                  <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                    No playlist group assigned
                  </Typography>
                </Box>
              )}
            </SignageSection>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default DeviceView;
